import * as React from "react"
import { useState, useEffect, useCallback, useMemo } from "react"
import { motion, AnimatePresence } from "motion/react"
import confetti from "canvas-confetti"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from "@/components/ui/card"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ArrowLeft, ArrowRight, Sparkles, User, Building2, AlertCircle, Copy, Check, Star } from "lucide-react"
import { AgentIcon, SoloAgentIcon, AgencyIcon, AnimatedCheckmark } from "@/components/AnimatedIcons"
// Casino dark theme color palette
const colors = {
  bg: "#121220",
  surface: "#1C1C32",
  surfaceHover: "#222240",
  inputBg: "#1A1A30",
  border: "#2A2A45",
  borderHover: "#3A3A55",
  borderFocus: "#F37020",
  textPrimary: "#FFFFFF",
  textSecondary: "#9A9ABF",
  textMuted: "#6A6A8A",
  accent: "#F37020",
  accentHover: "#FF8C42",
  error: "#FF4757",
  errorBg: "rgba(255, 71, 87, 0.1)",
  success: "#32CD32",
  successBg: "rgba(50, 205, 50, 0.1)",
  glow: "rgba(243, 112, 32, 0.3)",
}

import { useAuth } from "@/contexts/AuthContext"
import { z } from "zod"
import ovalMoneyClubLogo from "@/public/oval-money-club-logo.png"
import ovalLogo from "@/public/oval-logo.svg"
import cashappLogoImg from "@/public/cashapp-logo.png"

type UserType = "agent" | "solo_agent" | "agency" | null
type PaymentMethod = "cashapp" | "venmo" | "zelle" | "paypal"

type TaxEntityType = "individual" | "business" | null

interface W9Info {
  taxEntityType: TaxEntityType
  legalName: string
  businessName: string
  taxClassification: string
  address: string
  city: string
  state: string
  zip: string
  ssn: string
  ein: string
}

interface PaymentInfo {
  methods: PaymentMethod[]
  cashappHandle: string
  venmoHandle: string
  zelleEmail: string
  paypalEmail: string
}

interface OnboardingData {
  userType: UserType
  agencyId: string
  isIndependent: boolean
  w9Info: W9Info
  displayName: string
  agencyName: string
  collectOnBehalf: boolean
  paymentInfo: PaymentInfo
}

const initialW9Info: W9Info = {
  taxEntityType: null,
  legalName: "",
  businessName: "",
  taxClassification: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  ssn: "",
  ein: "",
}

const initialPaymentInfo: PaymentInfo = {
  methods: [],
  cashappHandle: "",
  venmoHandle: "",
  zelleEmail: "",
  paypalEmail: "",
}

const initialData: OnboardingData = {
  userType: null,
  agencyId: "",
  isIndependent: false,
  w9Info: initialW9Info,
  displayName: "",
  agencyName: "",
  collectOnBehalf: false,
  paymentInfo: initialPaymentInfo,
}

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
] as const

const US_STATE_VALUE_SET: ReadonlySet<string> = new Set(US_STATES.map((state) => state.value))
const PAYMENT_METHOD_VALUES = ["cashapp", "venmo", "zelle", "paypal"] as const
const PAYMENT_METHOD_SET: ReadonlySet<PaymentMethod> = new Set(PAYMENT_METHOD_VALUES)
const PAYMENT_METHOD_FIELD_MAP: Record<PaymentMethod, keyof PaymentInfo> = {
  cashapp: "cashappHandle",
  venmo: "venmoHandle",
  zelle: "zelleEmail",
  paypal: "paypalEmail",
}
const CASH_APP_HANDLE_REGEX = /^\$[A-Za-z][A-Za-z0-9_]{0,19}$/
const VENMO_HANDLE_REGEX = /^@[A-Za-z0-9_-]{1,30}$/
const AGENT_USERNAME_REGEX = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/
const emailAddressSchema = z.string().email("Enter a valid email address")
const displayNameSchema = z
  .string()
  .trim()
  .min(2, "Display name must be at least 2 characters")
  .max(50, "Display name must be 50 characters or less")
const agentUsernameSchema = z
  .string()
  .trim()
  .min(3, "Username must be at least 3 characters")
  .max(32, "Username must be 32 characters or less")
  .regex(
    AGENT_USERNAME_REGEX,
    "Use only lowercase letters, numbers, hyphens (-), or underscores (_); no spaces"
  )
const agencyNameSchema = z
  .string()
  .trim()
  .min(2, "Agency name must be at least 2 characters")
  .max(80, "Agency name must be 80 characters or less")
const agencyIdSchema = z
  .string()
  .trim()
  .regex(/^OVL-[A-Z0-9]{8}$/, "Agency ID must match OVL-XXXXXXXX")

const W9_FIELD_KEYS: Array<keyof W9Info> = [
  "taxEntityType",
  "legalName",
  "businessName",
  "taxClassification",
  "address",
  "city",
  "state",
  "zip",
  "ssn",
  "ein",
]

const commonW9Schema = z.object({
  legalName: z.string().min(1, "Legal name is required"),
  address: z.string().min(1, "Street address is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().refine((value) => US_STATE_VALUE_SET.has(value), "Select a valid US state"),
  zip: z.string().regex(/^\d{5}(-\d{4})?$/, "Enter a valid ZIP code"),
  taxClassification: z.string().min(1, "Tax classification is required"),
})

const w9Schema = z.discriminatedUnion("taxEntityType", [
  commonW9Schema.extend({
    taxEntityType: z.literal("individual"),
    businessName: z.string().optional(),
    ssn: z.string().regex(/^\d{9}$/, "SSN must be 9 digits"),
    ein: z.string().optional(),
  }),
  commonW9Schema.extend({
    taxEntityType: z.literal("business"),
    businessName: z.string().min(1, "Business name is required"),
    ssn: z.string().optional(),
    ein: z.string().regex(/^\d{9}$/, "EIN must be 9 digits"),
  }),
])

const prepareW9ForValidation = (w9Info: W9Info) => ({
  ...w9Info,
  legalName: w9Info.legalName.trim(),
  businessName: w9Info.businessName.trim(),
  taxClassification: w9Info.taxClassification.trim(),
  address: w9Info.address.trim(),
  city: w9Info.city.trim(),
  state: w9Info.state.trim().toUpperCase(),
  zip: w9Info.zip.trim(),
  ssn: w9Info.ssn.replace(/\D/g, ""),
  ein: w9Info.ein.replace(/\D/g, ""),
})

const normalizeAgentUsernameInput = (value: string) =>
  value
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9_-]/g, "")

const getDisplayNameValidationSchema = (userType: UserType) =>
  userType === "agent" ? agentUsernameSchema : displayNameSchema

const getW9FieldErrors = (w9Info: W9Info): Record<string, string> => {
  const preparedW9Info = prepareW9ForValidation(w9Info)
  const result = w9Schema.safeParse(preparedW9Info)

  if (result.success) {
    return {}
  }

  const nextErrors: Record<string, string> = {}
  for (const issue of result.error.issues) {
    const field = issue.path[0]
    if (typeof field !== "string" || nextErrors[field]) continue
    nextErrors[field] = issue.message
  }

  if (!preparedW9Info.taxEntityType) {
    nextErrors.taxEntityType = "Select whether you are filing as an individual or business"
  }

  return nextErrors
}

const normalizeCashAppHandle = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return ""

  const normalized = trimmed.replace(/^\$+/, "").replace(/\s+/g, "")
  return normalized ? `$${normalized}` : ""
}

const normalizeVenmoHandle = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return ""

  const normalized = trimmed.replace(/^@+/, "").replace(/\s+/g, "")
  return normalized ? `@${normalized}` : ""
}

const normalizePaymentEmail = (value: string): string => value.trim().toLowerCase()

const preparePaymentForValidation = (paymentInfo: PaymentInfo): PaymentInfo => ({
  methods: Array.from(new Set(paymentInfo.methods)).filter((method): method is PaymentMethod =>
    PAYMENT_METHOD_SET.has(method)
  ),
  cashappHandle: normalizeCashAppHandle(paymentInfo.cashappHandle),
  venmoHandle: normalizeVenmoHandle(paymentInfo.venmoHandle),
  zelleEmail: normalizePaymentEmail(paymentInfo.zelleEmail),
  paypalEmail: normalizePaymentEmail(paymentInfo.paypalEmail),
})

const paymentSchema = z
  .object({
    methods: z.array(z.enum(PAYMENT_METHOD_VALUES)).min(1, "Select at least one payment method"),
    cashappHandle: z.string(),
    venmoHandle: z.string(),
    zelleEmail: z.string(),
    paypalEmail: z.string(),
  })
  .superRefine((paymentInfo, ctx) => {
    if (paymentInfo.methods.includes("cashapp") && !CASH_APP_HANDLE_REGEX.test(paymentInfo.cashappHandle)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["cashappHandle"],
        message: "Enter a valid Cash App handle (example: $cashtag)",
      })
    }

    if (paymentInfo.methods.includes("venmo") && !VENMO_HANDLE_REGEX.test(paymentInfo.venmoHandle)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["venmoHandle"],
        message: "Enter a valid Venmo handle (example: @username)",
      })
    }

    if (paymentInfo.methods.includes("zelle") && !emailAddressSchema.safeParse(paymentInfo.zelleEmail).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["zelleEmail"],
        message: "Enter a valid Zelle email address",
      })
    }

    if (paymentInfo.methods.includes("paypal") && !emailAddressSchema.safeParse(paymentInfo.paypalEmail).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["paypalEmail"],
        message: "Enter a valid PayPal email address",
      })
    }
  })

const getPaymentFieldErrors = (paymentInfo: PaymentInfo): Record<string, string> => {
  const preparedPaymentInfo = preparePaymentForValidation(paymentInfo)
  const result = paymentSchema.safeParse(preparedPaymentInfo)

  if (result.success) {
    return {}
  }

  const nextErrors: Record<string, string> = {}
  for (const issue of result.error.issues) {
    const field = issue.path[0]
    if (typeof field !== "string" || nextErrors[field]) continue
    nextErrors[field] = issue.message
  }

  return nextErrors
}

function generateAgencyId(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
  let result = "OVL-"
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

// Dark elegant animation variants - subtle, no spring bounce
const pageVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
}

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
    },
  },
}

const fadeInUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
}

// Dark Elegant Input component - no spring animations
function AnimatedInput({
  className = "",
  ...props
}: React.ComponentProps<typeof Input>) {
  const [isFocused, setIsFocused] = useState(false)

  return (
    <Input
      {...props}
      className={`
        h-12 text-base font-medium
        bg-[#1C1C32] border border-[#2A2A45] rounded-xl
        text-white placeholder:text-[#6A6A8A]
        transition-all duration-200 ease-out
        ${isFocused
          ? "border-[#F37020]"
          : "hover:border-[#3A3A55]"
        }
        ${className}
      `}
      onFocus={(e) => {
        setIsFocused(true)
        props.onFocus?.(e)
      }}
      onBlur={(e) => {
        setIsFocused(false)
        props.onBlur?.(e)
      }}
    />
  )
}

// Dark Elegant Button - orange gradient, subtle hover
function GlowButton({
  children,
  variant = "default",
  className = "",
  ...props
}: React.ComponentProps<typeof Button>) {
  const isOutline = variant === "outline"

  return (
    <motion.div
      className="relative"
      whileHover={props.disabled ? {} : { scale: 1.02 }}
      whileTap={props.disabled ? {} : { scale: 0.98 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
    >
      <Button
        variant={variant}
        className={`
          relative overflow-hidden font-semibold text-base
          rounded-xl transition-all duration-200
          ${isOutline
            ? "bg-transparent border border-[#2A2A45] text-white hover:bg-[#1C1C32] hover:border-[#3A3A55]"
            : "bg-gradient-to-r from-[#F37020] to-[#FF8C42] text-white border-0 hover:brightness-110"
          }
          disabled:opacity-50 disabled:cursor-not-allowed
          ${className}
        `}
        {...props}
      >
        {children}
      </Button>
    </motion.div>
  )
}

// Dark Elegant Selection Card - subtle borders, orange selection
function SelectionCard({
  children,
  isSelected,
  onClick,
  delay = 0,
}: {
  children: React.ReactNode
  isSelected: boolean
  onClick: () => void
  delay?: number
}) {
  return (
    <motion.div
      variants={fadeInUp}
      initial="initial"
      animate="animate"
      transition={{ delay, duration: 0.2, ease: "easeOut" }}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={onClick}
      className={`
        relative cursor-pointer rounded-xl border p-5 transition-all duration-200
        bg-[#1C1C32] 
        ${isSelected
          ? "border-[#F37020] shadow-[0_0_16px_rgba(196,160,82,0.12)]"
          : "border-[#2A2A45] hover:border-[#3A3A55]"
        }
      `}
    >
      {/* Selected checkmark - orange */}
      <AnimatePresence>
        {isSelected && (
          <motion.div
            className="absolute -top-2 -right-2 w-7 h-7 bg-[#F37020] rounded-full flex items-center justify-center"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
          >
            <Check className="w-4 h-4 text-white" strokeWidth={3} />
          </motion.div>
        )}
      </AnimatePresence>

      {children}
    </motion.div>
  )
}

// Step Components
function UserTypeStep({
  data,
  onUpdate,
}: {
  data: OnboardingData
  onUpdate: (updates: Partial<OnboardingData>) => void
}) {
  const userTypes = [
    {
      value: "agent" as const,
      label: "Agent",
      description: "I work under an existing agency",
      icon: AgentIcon,
      color: "primary",
    },
    {
      value: "solo_agent" as const,
      label: "Solo Agent",
      description: "I work independently as my own agency",
      icon: SoloAgentIcon,
      color: "accent",
    },
    {
      value: "agency" as const,
      label: "Agency",
      description: "I manage a team of agents",
      icon: AgencyIcon,
      color: "yellow",
    },
  ]

  return (
    <motion.div
      className="space-y-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.div className="text-center space-y-2" variants={fadeInUp}>
        <h2 className="text-2xl font-semibold text-white">
          Welcome to Oval
        </h2>
        <p className="text-[#9A9ABF]">
          Let's get you set up. First, tell us who you are.
        </p>
      </motion.div>

      <RadioGroup
        value={data.userType || ""}
        onValueChange={(value) => onUpdate({
          userType: value as UserType,
          ...(value === "solo_agent" && { isIndependent: true })
        })}
        className="grid gap-4"
      >
        {userTypes.map((type, index) => (
          <SelectionCard
            key={type.value}
            isSelected={data.userType === type.value}
            onClick={() => onUpdate({
              userType: type.value,
              ...(type.value === "solo_agent" && { isIndependent: true })
            })}
            delay={index * 0.05}
          >
            <div className="flex items-center gap-4">
              <RadioGroupItem value={type.value} id={type.value} className="sr-only" />
              <div className="size-14 rounded-xl bg-[#1C1C32] flex items-center justify-center">
                <type.icon isActive={data.userType === type.value} />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-lg text-white">{type.label}</div>
                <div className="text-sm text-[#9A9ABF]">
                  {type.description}
                </div>
              </div>
              <motion.div
                className="size-6 rounded-full border-2 flex items-center justify-center"
                animate={{
                  borderColor:
                    data.userType === type.value ? "#F37020" : "rgba(255,255,255,0.2)",
                  backgroundColor:
                    data.userType === type.value ? "#F37020" : "transparent",
                }}
                transition={{ duration: 0.15 }}
              >
                {data.userType === type.value && (
                  <motion.div
                    className="size-2 bg-white rounded-full"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.1 }}
                  />
                )}
              </motion.div>
            </div>
          </SelectionCard>
        ))}
      </RadioGroup>
    </motion.div>
  )
}

function AgencyIdStep({
  data,
  onUpdate,
  agencyValidation,
  onValidateAgency,
}: {
  data: OnboardingData
  onUpdate: (updates: Partial<OnboardingData>) => void
  agencyValidation: { isValid: boolean | null; agencyName: string | null; error: string | null; isValidating: boolean }
  onValidateAgency: (agencyId: string) => void
}) {
  const handleAgencyIdChange = (value: string) => {
    const uppercaseValue = value.toUpperCase()
    onUpdate({ agencyId: uppercaseValue })
    if (uppercaseValue.length >= 12) {
      onValidateAgency(uppercaseValue)
    }
  }

  return (
    <motion.div
      className="space-y-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.div className="text-center space-y-2" variants={fadeInUp}>
        <h2 className="text-2xl font-semibold text-white">Agency Information</h2>
        <p className="text-[#9A9ABF]">
          {data.userType === "agent"
            ? "Enter your agency's ID to join their team"
            : "Are you part of an existing agency or independent?"}
        </p>
      </motion.div>

      {data.userType === "agent" ? (
        <motion.div className="space-y-4" variants={fadeInUp}>
          <div className="space-y-2">
            <Label htmlFor="agencyId" className="text-[#9A9ABF]">Agency ID</Label>
            <AnimatedInput
              id="agencyId"
              placeholder="e.g., OVL-ABC12345"
              value={data.agencyId}
              onChange={(e) => handleAgencyIdChange(e.target.value)}
              autoCapitalize="characters"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              className={`text-center text-base tracking-widest font-mono ${
                agencyValidation.isValid === false ? "border-[#FF4757]" :
                agencyValidation.isValid === true ? "border-[#32CD32]" : ""
              }`}
            />

            {agencyValidation.isValidating && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-center gap-2 text-sm text-[#9A9ABF]"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-4 h-4 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full"
                />
                Validating...
              </motion.div>
            )}

            {agencyValidation.isValid === true && agencyValidation.agencyName && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 bg-[rgba(129,199,132,0.1)] border border-[rgba(129,199,132,0.3)] rounded-xl text-center"
              >
                <div className="flex items-center justify-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#32CD32]" />
                  <span className="text-[#32CD32] font-medium">Valid Agency ID!</span>
                </div>
                <p className="text-[#4AE54A] font-semibold mt-1">{agencyValidation.agencyName}</p>
              </motion.div>
            )}

            {agencyValidation.isValid === false && agencyValidation.error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 bg-[rgba(229,115,115,0.1)] border border-[rgba(229,115,115,0.3)] rounded-xl text-center"
              >
                <div className="flex items-center justify-center gap-2">
                  <AlertCircle className="w-4 h-4 text-[#FF4757]" />
                  <span className="text-[#FF4757] font-medium">{agencyValidation.error}</span>
                </div>
              </motion.div>
            )}

            {!agencyValidation.isValidating && agencyValidation.isValid === null && (
              <p className="text-sm text-[#6A6A8A] text-center">
                Ask your agency manager for this ID
              </p>
            )}
          </div>
        </motion.div>
      ) : (
        <RadioGroup
          value={data.isIndependent ? "independent" : "existing"}
          onValueChange={(value) =>
            onUpdate({ isIndependent: value === "independent" })
          }
          className="grid gap-4"
        >
          <SelectionCard
            isSelected={!data.isIndependent}
            onClick={() => onUpdate({ isIndependent: false })}
            delay={0}
          >
            <div className="flex items-center gap-4">
              <RadioGroupItem value="existing" id="existing" className="sr-only" />
              <div className="flex-1">
                <div className="font-semibold text-white">Join Existing Agency</div>
                <div className="text-sm text-[#9A9ABF]">
                  I have an agency ID to join
                </div>
              </div>
            </div>
          </SelectionCard>

          <SelectionCard
            isSelected={data.isIndependent}
            onClick={() => onUpdate({ isIndependent: true })}
            delay={0.05}
          >
            <div className="flex items-center gap-4">
              <RadioGroupItem value="independent" id="independent" className="sr-only" />
              <div className="flex-1">
                <div className="font-semibold text-white">I'm Independent</div>
                <div className="text-sm text-[#9A9ABF]">
                  I'll create my own agency
                </div>
              </div>
            </div>
          </SelectionCard>
        </RadioGroup>
      )}

      {!data.isIndependent && data.userType !== "agent" && (
        <motion.div
          className="space-y-2"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
        >
          <Label htmlFor="agencyIdInput" className="text-[#9A9ABF]">Agency ID</Label>
          <AnimatedInput
            id="agencyIdInput"
            placeholder="e.g., OVL-ABC12345"
            value={data.agencyId}
            onChange={(e) => handleAgencyIdChange(e.target.value)}
            autoCapitalize="characters"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            className={`text-center text-base tracking-widest font-mono ${
              agencyValidation.isValid === false ? "border-[#FF4757]" :
              agencyValidation.isValid === true ? "border-[#32CD32]" : ""
            }`}
          />

          {agencyValidation.isValidating && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-center gap-2 text-sm text-[#9A9ABF]"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-4 h-4 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full"
              />
              Validating...
            </motion.div>
          )}

          {agencyValidation.isValid === true && agencyValidation.agencyName && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 bg-[rgba(129,199,132,0.1)] border border-[rgba(129,199,132,0.3)] rounded-xl text-center"
            >
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4 text-[#32CD32]" />
                <span className="text-[#32CD32] font-medium">Valid Agency ID!</span>
              </div>
              <p className="text-[#4AE54A] font-semibold mt-1">{agencyValidation.agencyName}</p>
            </motion.div>
          )}

          {agencyValidation.isValid === false && agencyValidation.error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 bg-[rgba(229,115,115,0.1)] border border-[rgba(229,115,115,0.3)] rounded-xl text-center"
            >
              <div className="flex items-center justify-center gap-2">
                <AlertCircle className="w-4 h-4 text-[#FF4757]" />
                <span className="text-[#FF4757] font-medium">{agencyValidation.error}</span>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </motion.div>
  )
}

function W9Step({
  data,
  onUpdate,
}: {
  data: OnboardingData
  onUpdate: (updates: Partial<OnboardingData>) => void
}) {
  const [w9Errors, setW9Errors] = useState<Record<string, string>>({})
  const [w9Touched, setW9Touched] = useState<Record<string, boolean>>({})

  const validateField = useCallback((field: keyof W9Info, nextW9Info: W9Info = data.w9Info) => {
    const nextFieldErrors = getW9FieldErrors(nextW9Info)

    setW9Errors((prev) => {
      const updated = { ...prev }
      if (nextFieldErrors[field]) {
        updated[field] = nextFieldErrors[field]
      } else {
        delete updated[field]
      }
      return updated
    })
  }, [data.w9Info])

  const validateAllFields = useCallback((nextW9Info: W9Info = data.w9Info): boolean => {
    setW9Touched(W9_FIELD_KEYS.reduce((acc, field) => ({ ...acc, [field]: true }), {} as Record<string, boolean>))
    const nextFieldErrors = getW9FieldErrors(nextW9Info)
    setW9Errors(nextFieldErrors)
    return Object.keys(nextFieldErrors).length === 0
  }, [data.w9Info])

  useEffect(() => {
    if (Object.keys(w9Touched).length === 0) return

    const nextFieldErrors = getW9FieldErrors(data.w9Info)
    const nextTouchedErrors: Record<string, string> = {}
    for (const [field, touched] of Object.entries(w9Touched)) {
      if (touched && nextFieldErrors[field]) {
        nextTouchedErrors[field] = nextFieldErrors[field]
      }
    }
    setW9Errors(nextTouchedErrors)
  }, [data.w9Info, w9Touched])

  const markTouched = (field: keyof W9Info) => {
    setW9Touched((prev) => ({ ...prev, [field]: true }))
  }

  const handleFieldBlur = (field: keyof W9Info) => {
    markTouched(field)
    validateField(field)
  }

  const updateW9 = (field: keyof W9Info, value: string | TaxEntityType) => {
    const nextW9Info = { ...data.w9Info, [field]: value }
    onUpdate({ w9Info: nextW9Info })

    if (w9Touched[field]) {
      validateField(field, nextW9Info)
    }
  }

  const updateSelectField = (field: keyof W9Info, value: string) => {
    const nextW9Info = { ...data.w9Info, [field]: value }
    onUpdate({ w9Info: nextW9Info })
    markTouched(field)
    validateField(field, nextW9Info)
  }

  const handleEntityTypeChange = (nextEntityType: Exclude<TaxEntityType, null>) => {
    const nextW9Info: W9Info = nextEntityType === "individual"
      ? {
        ...data.w9Info,
        taxEntityType: "individual",
        ein: "",
        taxClassification: "individual",
      }
      : {
        ...data.w9Info,
        taxEntityType: "business",
        ssn: "",
        taxClassification: "",
      }

    onUpdate({ w9Info: nextW9Info })
    markTouched("taxEntityType")
    validateField("taxEntityType", nextW9Info)
  }

  const hasFieldError = (field: keyof W9Info): boolean => Boolean(w9Touched[field] && w9Errors[field])
  const renderFieldError = (field: keyof W9Info) => {
    if (!hasFieldError(field)) return null

    return (
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center gap-1 text-[#FF4757] text-xs"
      >
        <AlertCircle className="size-3" />
        {w9Errors[field]}
      </motion.div>
    )
  }

  const formatSSN = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 9)
    if (digits.length <= 3) return digits
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
  }

  const formatEIN = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 9)
    if (digits.length <= 2) return digits
    return `${digits.slice(0, 2)}-${digits.slice(2)}`
  }

  const isValidSSN = (ssn: string): boolean => ssn.replace(/\D/g, "").length === 9
  const isValidEIN = (ein: string): boolean => ein.replace(/\D/g, "").length === 9

  const entityType = data.w9Info.taxEntityType

  return (
    <motion.div
      className="space-y-5"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.div className="text-center space-y-2" variants={fadeInUp}>
        <h2 className="text-2xl font-semibold text-white">Tax Information (W9)</h2>
        <p className="text-[#9A9ABF]">
          Required to send you bonuses and payouts
        </p>
      </motion.div>

      {/* Entity Type Selection */}
      <motion.div className="space-y-3" variants={fadeInUp}>
        <Label className="text-sm font-medium text-[#9A9ABF]">Are you filing as an individual or business? *</Label>
        <div className="grid grid-cols-2 gap-3">
          <SelectionCard
            isSelected={entityType === "individual"}
            onClick={() => handleEntityTypeChange("individual")}
            delay={0}
          >
            <div className="flex items-center gap-3 py-1">
              <div className="size-10 rounded-xl bg-[#1C1C32] flex items-center justify-center">
                <User className={`size-5 ${entityType === "individual" ? "text-[#F37020]" : "text-[#6A6A8A]"}`} />
              </div>
              <div className="flex-1">
                <div className="font-medium text-sm text-white">Individual</div>
                <div className="text-xs text-white/50">SSN required</div>
              </div>
            </div>
          </SelectionCard>

          <SelectionCard
            isSelected={entityType === "business"}
            onClick={() => handleEntityTypeChange("business")}
            delay={0.05}
          >
            <div className="flex items-center gap-3 py-1">
              <div className="size-10 rounded-xl bg-[#1C1C32] flex items-center justify-center">
                <Building2 className={`size-5 ${entityType === "business" ? "text-[#F37020]" : "text-[#6A6A8A]"}`} />
              </div>
              <div className="flex-1">
                <div className="font-medium text-sm text-white">Business</div>
                <div className="text-xs text-white/50">EIN required</div>
              </div>
            </div>
          </SelectionCard>
        </div>
        {renderFieldError("taxEntityType")}
      </motion.div>

      <AnimatePresence>
        {entityType && (
          <motion.div
            className="grid gap-4"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="legalName" className="text-sm text-[#9A9ABF]">
                  {entityType === "individual" ? "Full Legal Name *" : "Legal Name *"}
                </Label>
                <AnimatedInput
                  id="legalName"
                  placeholder={entityType === "individual" ? "John Doe" : "Jane Smith"}
                  value={data.w9Info.legalName}
                  onChange={(e) => updateW9("legalName", e.target.value)}
                  onBlur={() => handleFieldBlur("legalName")}
                  autoComplete="name"
                  autoCapitalize="words"
                  autoCorrect="off"
                  spellCheck={false}
                  className={`text-base ${hasFieldError("legalName") ? "border-[#FF4757]" : ""}`}
                />
                {renderFieldError("legalName")}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="businessName" className="text-sm text-[#9A9ABF]">
                  {entityType === "business" ? "Business Name *" : "Business Name"}
                </Label>
                <AnimatedInput
                  id="businessName"
                  placeholder="Doe Insurance LLC"
                  value={data.w9Info.businessName}
                  onChange={(e) => updateW9("businessName", e.target.value)}
                  onBlur={() => handleFieldBlur("businessName")}
                  autoComplete="organization"
                  autoCapitalize="words"
                  autoCorrect="off"
                  spellCheck={false}
                  className={`text-base ${hasFieldError("businessName") ? "border-[#FF4757]" : ""}`}
                />
                {renderFieldError("businessName")}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="taxClassification" className="text-sm text-[#9A9ABF]">Tax Classification *</Label>
              <Select
                value={data.w9Info.taxClassification}
                onValueChange={(value) => updateSelectField("taxClassification", value)}
              >
                <SelectTrigger
                  className={`h-12 text-base font-medium bg-[#1C1C32] border border-[#2A2A45] rounded-xl text-white transition-all hover:border-[#3A3A55] focus:border-[#F37020] focus:ring-1 focus:ring-[#F37020]/30 ${hasFieldError("taxClassification") ? "border-[#FF4757]" : ""}`}
                  onBlur={() => handleFieldBlur("taxClassification")}
                >
                  <SelectValue placeholder="Select classification" className="text-[#6A6A8A]" />
                </SelectTrigger>
                <SelectContent className="bg-[#121220] border-[#2A2A45]">
                  {entityType === "individual" ? (
                    <SelectItem value="individual" className="text-white hover:bg-[#1A1A30]">Individual/Sole Proprietor</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="c_corp" className="text-white hover:bg-[#1A1A30]">C Corporation</SelectItem>
                      <SelectItem value="s_corp" className="text-white hover:bg-[#1A1A30]">S Corporation</SelectItem>
                      <SelectItem value="partnership" className="text-white hover:bg-[#1A1A30]">Partnership</SelectItem>
                      <SelectItem value="llc_c" className="text-white hover:bg-[#1A1A30]">LLC (taxed as C Corp)</SelectItem>
                      <SelectItem value="llc_s" className="text-white hover:bg-[#1A1A30]">LLC (taxed as S Corp)</SelectItem>
                      <SelectItem value="llc_p" className="text-white hover:bg-[#1A1A30]">LLC (taxed as Partnership)</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              {renderFieldError("taxClassification")}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="address" className="text-sm text-[#9A9ABF]">Street Address *</Label>
              <AnimatedInput
                id="address"
                placeholder="123 Main St"
                value={data.w9Info.address}
                onChange={(e) => updateW9("address", e.target.value)}
                onBlur={() => handleFieldBlur("address")}
                autoComplete="street-address"
                autoCapitalize="words"
                autoCorrect="off"
                spellCheck={false}
                className={`text-base ${hasFieldError("address") ? "border-[#FF4757]" : ""}`}
              />
              {renderFieldError("address")}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div className="space-y-1.5 col-span-2 sm:col-span-1">
                <Label htmlFor="city" className="text-sm text-[#9A9ABF]">City *</Label>
                <AnimatedInput
                  id="city"
                  placeholder="New York"
                  value={data.w9Info.city}
                  onChange={(e) => updateW9("city", e.target.value)}
                  onBlur={() => handleFieldBlur("city")}
                  autoComplete="address-level2"
                  autoCapitalize="words"
                  autoCorrect="off"
                  spellCheck={false}
                  className={`text-base ${hasFieldError("city") ? "border-[#FF4757]" : ""}`}
                />
                {renderFieldError("city")}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="state" className="text-sm text-[#9A9ABF]">State *</Label>
                <Select
                  value={data.w9Info.state}
                  onValueChange={(value) => updateSelectField("state", value)}
                >
                  <SelectTrigger
                    id="state"
                    className={`h-12 bg-[#1C1C32] border border-[#2A2A45] rounded-xl text-white transition-all hover:border-[#3A3A55] focus:border-[#F37020] focus:ring-1 focus:ring-[#F37020]/30 ${hasFieldError("state") ? "border-[#FF4757]" : ""}`}
                    onBlur={() => handleFieldBlur("state")}
                  >
                    <SelectValue placeholder="Select" className="text-[#6A6A8A]" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72 bg-[#121220] border-[#2A2A45]">
                    {US_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value} className="text-white hover:bg-[#1A1A30]">
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {renderFieldError("state")}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="zip" className="text-sm text-[#9A9ABF]">ZIP *</Label>
                <AnimatedInput
                  id="zip"
                  placeholder="10001"
                  maxLength={10}
                  inputMode="numeric"
                  value={data.w9Info.zip}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d-]/g, "")
                    updateW9("zip", value)
                  }}
                  onBlur={() => handleFieldBlur("zip")}
                  autoComplete="postal-code"
                  className={`text-base ${hasFieldError("zip") ? "border-[#FF4757]" : ""}`}
                />
                {renderFieldError("zip")}
              </div>
            </div>

            {/* SSN or EIN based on entity type */}
            <div className="space-y-1.5">
              {entityType === "individual" ? (
                <>
                  <Label htmlFor="ssn" className="text-sm text-[#9A9ABF]">Social Security Number (SSN) *</Label>
                  <AnimatedInput
                    id="ssn"
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter 9 digits"
                    value={data.w9Info.ssn}
                    onChange={(e) => updateW9("ssn", formatSSN(e.target.value))}
                    onBlur={() => handleFieldBlur("ssn")}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    className={`text-base ${hasFieldError("ssn") ? "border-[#FF4757]" : ""}`}
                  />
                  <p className="text-xs text-[#6A6A8A]">Just type digits, dashes are added automatically</p>
                  <div className="flex items-center gap-2">
                    {renderFieldError("ssn")}
                    {isValidSSN(data.w9Info.ssn) && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center gap-1 text-[#32CD32] text-xs"
                      >
                        <Sparkles className="size-3" />
                        Valid SSN
                      </motion.div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Label htmlFor="ein" className="text-sm text-[#9A9ABF]">Employer Identification Number (EIN) *</Label>
                  <AnimatedInput
                    id="ein"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter 9 digits"
                    value={data.w9Info.ein}
                    onChange={(e) => updateW9("ein", formatEIN(e.target.value))}
                    onBlur={() => handleFieldBlur("ein")}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    className={`text-base ${hasFieldError("ein") ? "border-[#FF4757]" : ""}`}
                  />
                  <p className="text-xs text-[#6A6A8A]">Just type digits, dashes are added automatically</p>
                  <div className="flex items-center gap-2">
                    {renderFieldError("ein")}
                    {isValidEIN(data.w9Info.ein) && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center gap-1 text-[#32CD32] text-xs"
                      >
                        <Sparkles className="size-3" />
                        Valid EIN
                      </motion.div>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

function DisplayNameStep({
  data,
  onUpdate,
}: {
  data: OnboardingData
  onUpdate: (updates: Partial<OnboardingData>) => void
}) {
  const [displayNameTouched, setDisplayNameTouched] = useState(false)
  const [displayNameError, setDisplayNameError] = useState<string | null>(null)
  const isAgentUsername = data.userType === "agent"

  const validateDisplayName = (value: string) => {
    const result = getDisplayNameValidationSchema(data.userType).safeParse(value)
    setDisplayNameError(
      result.success
        ? null
        : result.error.issues[0]?.message || (isAgentUsername ? "Username is required" : "Display name is required")
    )
  }

  useEffect(() => {
    if (!displayNameTouched) return
    validateDisplayName(data.displayName)
  }, [data.displayName, displayNameTouched])

  const hasDisplayNameError = displayNameTouched && !!displayNameError

  return (
    <motion.div
      className="space-y-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.div className="text-center space-y-2" variants={fadeInUp}>
        <h2 className="text-2xl font-semibold text-white">{isAgentUsername ? "Username" : "Display Name"}</h2>
        <p className="text-[#9A9ABF]">
          {isAgentUsername
            ? "Choose a username with letters, numbers, hyphens, or underscores only"
            : "This name will be shown in internal chats and leaderboards"}
        </p>
      </motion.div>

      <motion.div className="space-y-3" variants={fadeInUp}>
        <Label htmlFor="displayName" className="text-[#9A9ABF]">
          {isAgentUsername ? "Username *" : "Internal Display Name *"}
        </Label>
        <AnimatedInput
          id="displayName"
          placeholder={isAgentUsername ? "e.g., pizza-bots or insurance_assassins" : "e.g., John D. or JDoe"}
          value={data.displayName}
          onChange={(e) =>
            onUpdate({
              displayName: isAgentUsername
                ? normalizeAgentUsernameInput(e.target.value)
                : e.target.value,
            })
          }
          onBlur={() => {
            setDisplayNameTouched(true)
            validateDisplayName(data.displayName)
          }}
          autoCapitalize={isAgentUsername ? "none" : "words"}
          autoCorrect="off"
          autoComplete="nickname"
          spellCheck={false}
          className={`text-center text-base sm:text-xl ${hasDisplayNameError ? "border-[#FF4757]" : ""}`}
        />
        {hasDisplayNameError && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center justify-center gap-1 text-[#FF4757] text-xs"
          >
            <AlertCircle className="size-3" />
            {displayNameError}
          </motion.div>
        )}
        <p className="text-sm text-[#6A6A8A] text-center">
          {isAgentUsername
            ? "No spaces. Example: pizza-bots, insurance_assassins"
            : "This is how other agents and your agency will see you"}
        </p>
      </motion.div>
    </motion.div>
  )
}

function AgencyNameStep({
  data,
  onUpdate,
  nameStatus,
}: {
  data: OnboardingData
  onUpdate: (updates: Partial<OnboardingData>) => void
  nameStatus: { available: boolean | null; checking: boolean }
}) {
  const [agencyNameTouched, setAgencyNameTouched] = useState(false)
  const [agencyNameError, setAgencyNameError] = useState<string | null>(null)

  const validateAgencyName = (value: string) => {
    const result = agencyNameSchema.safeParse(value)
    setAgencyNameError(result.success ? null : result.error.issues[0]?.message || "Agency name is required")
  }

  useEffect(() => {
    if (!agencyNameTouched) return
    validateAgencyName(data.agencyName)
  }, [data.agencyName, agencyNameTouched])

  const hasAgencyNameError = agencyNameTouched && !!agencyNameError

  return (
    <motion.div
      className="space-y-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.div className="text-center space-y-2" variants={fadeInUp}>
        <h2 className="text-2xl font-semibold text-white">Team Name</h2>
        <p className="text-[#9A9ABF]">
          {data.userType === "agency"
            ? "Choose a name for your agency that your agents will see"
            : "Choose a display name for yourself"}
        </p>
      </motion.div>

      <motion.div className="space-y-3" variants={fadeInUp}>
        <Label htmlFor="agencyName" className="text-[#9A9ABF]">
          {data.userType === "agency" ? "Agency Name *" : "Display Name *"}
        </Label>
        <AnimatedInput
          id="agencyName"
          placeholder={data.userType === "agency" ? "e.g., Elite Insurance Group" : "e.g., John's Insurance"}
          value={data.agencyName}
          onChange={(e) => onUpdate({ agencyName: e.target.value })}
          onBlur={() => {
            setAgencyNameTouched(true)
            validateAgencyName(data.agencyName)
          }}
          autoCapitalize="words"
          autoCorrect="off"
          autoComplete="organization"
          spellCheck={false}
          className={`text-center text-base sm:text-xl ${
            hasAgencyNameError || nameStatus.available === false ? "border-[#FF4757]" :
            nameStatus.available === true ? "border-[#32CD32]" : ""
          }`}
        />

        {hasAgencyNameError && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center justify-center gap-1 text-[#FF4757] text-xs"
          >
            <AlertCircle className="size-3" />
            {agencyNameError}
          </motion.div>
        )}

        {nameStatus.checking && !hasAgencyNameError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center gap-2 text-sm text-[#9A9ABF]"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-4 h-4 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full"
            />
            Checking availability...
          </motion.div>
        )}

        {nameStatus.available === true && !nameStatus.checking && !hasAgencyNameError && data.agencyName.trim().length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-2 text-sm text-[#32CD32]"
          >
            <Sparkles className="w-4 h-4" />
            Name is available!
          </motion.div>
        )}

        {nameStatus.available === false && !nameStatus.checking && !hasAgencyNameError && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-2 text-sm text-[#FF4757]"
          >
            <AlertCircle className="w-4 h-4" />
            This name is already taken
          </motion.div>
        )}

        <p className="text-sm text-[#6A6A8A] text-center">
          {data.userType === "agency"
            ? "This will be visible to your agents and on the leaderboard"
            : "This will be visible on your profile and the leaderboard"}
        </p>
      </motion.div>
    </motion.div>
  )
}

function PaymentCollectionStep({
  data,
  onUpdate,
}: {
  data: OnboardingData
  onUpdate: (updates: Partial<OnboardingData>) => void
}) {
  return (
    <motion.div
      className="space-y-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.div className="text-center space-y-2" variants={fadeInUp}>
        <h2 className="text-2xl font-semibold text-white">Payment Collection</h2>
        <p className="text-[#9A9ABF]">
          How would you like to handle agent payments?
        </p>
      </motion.div>

      <RadioGroup
        value={data.collectOnBehalf ? "collect" : "individual"}
        onValueChange={(value) => onUpdate({ collectOnBehalf: value === "collect" })}
        className="grid gap-4"
      >
        <SelectionCard
          isSelected={!data.collectOnBehalf}
          onClick={() => onUpdate({ collectOnBehalf: false })}
          delay={0}
        >
          <div className="flex items-center gap-4">
            <RadioGroupItem value="individual" id="individual" className="sr-only" />
            <div className="flex-1">
              <div className="font-semibold text-white">Agents Set Up Their Own</div>
              <div className="text-sm text-[#9A9ABF]">
                Each agent will provide their own payment info during their onboarding
              </div>
            </div>
          </div>
        </SelectionCard>

        <SelectionCard
          isSelected={data.collectOnBehalf}
          onClick={() => onUpdate({ collectOnBehalf: true })}
          delay={0.05}
        >
          <div className="flex items-center gap-4">
            <RadioGroupItem value="collect" id="collect" className="sr-only" />
            <div className="flex-1">
              <div className="font-semibold text-white">I'll Collect On Their Behalf</div>
              <div className="text-sm text-[#9A9ABF]">
                All agent payments will go to your account
              </div>
            </div>
          </div>
        </SelectionCard>
      </RadioGroup>
    </motion.div>
  )
}

function PaymentInfoStep({
  data,
  onUpdate,
  isAgency = false,
}: {
  data: OnboardingData
  onUpdate: (updates: Partial<OnboardingData>) => void
  isAgency?: boolean
}) {
  // Payment method logos as inline SVGs with brand colors
  const CashAppLogo = () => (
    <svg viewBox="3.5 3.5 13 18" className="w-7 h-7" fill="white">
      <path d="M13.16 6.085l-.27 1.37c.9.3 1.75.77 2.48 1.39l.02.02c.19.2.19.5 0 .68l-.93.93a.5.5 0 01-.67.01 5 5 0 00-3.22-1.18c-.97 0-1.94.32-1.94 1.21 0 .9 1.04 1.2 2.24 1.65 2.1.7 3.84 1.58 3.84 3.64 0 2.24-1.74 3.78-4.58 3.95l-.26 1.2a.49.49 0 01-.48.39H7.63l-.09-.01a.5.5 0 01-.38-.59l.28-1.27a6.54 6.54 0 01-2.88-1.57v-.01a.48.48 0 010-.68l1-.97a.49.49 0 01.67 0c.91.86 2.13 1.34 3.39 1.32 1.3 0 2.17-.55 2.17-1.42 0-.87-.88-1.1-2.54-1.72-1.76-.63-3.43-1.52-3.43-3.6 0-2.42 2.01-3.6 4.39-3.71l.25-1.23a.48.48 0 01.48-.38h1.78l.1.01c.26.06.43.31.37.57z"/>
    </svg>
  )

  const VenmoLogo = () => (
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="white">
      <path d="M19.78 2.2c.68 1.12 1 2.28 1 3.72 0 4.63-3.95 10.64-7.16 14.88H6.12L3.02 2.74l6.26-.58 1.65 13.3c1.53-2.5 3.43-6.42 3.43-9.1 0-1.37-.24-2.3-.65-3.05l6.07-1.11z"/>
    </svg>
  )

  const ZelleLogo = () => (
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="white">
      <path d="M13.559 24h-2.841a.483.483 0 0 1-.483-.483v-2.765H5.638a.667.667 0 0 1-.666-.666v-2.234a.67.67 0 0 1 .142-.412l8.139-10.382h-7.25a.667.667 0 0 1-.667-.667V3.914c0-.367.299-.666.666-.666h4.23V.483c0-.266.217-.483.483-.483h2.841c.266 0 .483.217.483.483v2.765h4.323c.367 0 .666.299.666.666v2.137a.67.67 0 0 1-.141.41l-8.19 10.481h7.665c.367 0 .666.299.666.666v2.477a.667.667 0 0 1-.666.667h-4.32v2.765a.483.483 0 0 1-.483.483Z"/>
    </svg>
  )

  const PayPalLogo = () => (
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="white">
      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082h-2.19c-1.307 0-2.398.976-2.606 2.245L5.89 24h5.67c.524 0 .968-.381 1.05-.9l.72-4.573a1.097 1.097 0 0 1 1.084-.9h.62c4.475 0 7.977-1.822 9.003-7.083.306-1.574.126-2.848-.815-3.627z"/>
    </svg>
  )

  const paymentMethods: {
    value: PaymentMethod
    label: string
    placeholder: string
    field: Exclude<keyof PaymentInfo, "methods">
    Logo: React.FC
    brandColor: string
  }[] = [
    { value: "cashapp", label: "Cash App", placeholder: "$cashtag", field: "cashappHandle", Logo: CashAppLogo, brandColor: "#00D632" },
    { value: "venmo", label: "Venmo", placeholder: "@username", field: "venmoHandle", Logo: VenmoLogo, brandColor: "#008CFF" },
    { value: "zelle", label: "Zelle", placeholder: "email@example.com", field: "zelleEmail", Logo: ZelleLogo, brandColor: "#6D1ED4" },
    { value: "paypal", label: "PayPal", placeholder: "email@example.com", field: "paypalEmail", Logo: PayPalLogo, brandColor: "#003087" },
  ]

  const [paymentErrors, setPaymentErrors] = useState<Record<string, string>>({})
  const [paymentTouched, setPaymentTouched] = useState<Record<string, boolean>>({})
  const preparedPaymentInfo = useMemo(() => preparePaymentForValidation(data.paymentInfo), [data.paymentInfo])

  const validateField = useCallback((field: keyof PaymentInfo, nextPaymentInfo: PaymentInfo = data.paymentInfo) => {
    const nextFieldErrors = getPaymentFieldErrors(nextPaymentInfo)
    setPaymentErrors((prev) => {
      const updated = { ...prev }
      if (nextFieldErrors[field]) {
        updated[field] = nextFieldErrors[field]
      } else {
        delete updated[field]
      }
      return updated
    })
  }, [data.paymentInfo])

  useEffect(() => {
    if (Object.keys(paymentTouched).length === 0) return

    const nextFieldErrors = getPaymentFieldErrors(data.paymentInfo)
    const nextTouchedErrors: Record<string, string> = {}
    for (const [field, touched] of Object.entries(paymentTouched)) {
      if (touched && nextFieldErrors[field]) {
        nextTouchedErrors[field] = nextFieldErrors[field]
      }
    }
    setPaymentErrors(nextTouchedErrors)
  }, [data.paymentInfo, paymentTouched])

  const markTouched = (field: keyof PaymentInfo) => {
    setPaymentTouched((prev) => ({ ...prev, [field]: true }))
  }

  const hasFieldError = (field: keyof PaymentInfo): boolean => Boolean(paymentTouched[field] && paymentErrors[field])
  const renderFieldError = (field: keyof PaymentInfo) => {
    if (!hasFieldError(field)) return null

    return (
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center gap-1 text-[#FF4757] text-xs"
      >
        <AlertCircle className="size-3" />
        {paymentErrors[field]}
      </motion.div>
    )
  }

  const isMethodValueValid = (method: PaymentMethod): boolean => {
    if (!preparedPaymentInfo.methods.includes(method)) return false

    switch (method) {
      case "cashapp":
        return CASH_APP_HANDLE_REGEX.test(preparedPaymentInfo.cashappHandle)
      case "venmo":
        return VENMO_HANDLE_REGEX.test(preparedPaymentInfo.venmoHandle)
      case "zelle":
        return emailAddressSchema.safeParse(preparedPaymentInfo.zelleEmail).success
      case "paypal":
        return emailAddressSchema.safeParse(preparedPaymentInfo.paypalEmail).success
      default:
        return false
    }
  }

  const toggleMethod = (method: PaymentMethod) => {
    const currentMethods = data.paymentInfo.methods
    const isSelected = currentMethods.includes(method)
    const newMethods = isSelected
      ? currentMethods.filter((m) => m !== method)
      : [...currentMethods, method]

    const nextPaymentInfo: PaymentInfo = {
      ...data.paymentInfo,
      methods: newMethods,
      cashappHandle:
        !isSelected && method === "cashapp" && data.paymentInfo.cashappHandle.trim().length === 0
          ? "$"
          : data.paymentInfo.cashappHandle,
      venmoHandle:
        !isSelected && method === "venmo" && data.paymentInfo.venmoHandle.trim().length === 0
          ? "@"
          : data.paymentInfo.venmoHandle,
    }

    onUpdate({ paymentInfo: nextPaymentInfo })
    setPaymentTouched((prev) => ({
      ...prev,
      methods: true,
      ...(!isSelected ? { [PAYMENT_METHOD_FIELD_MAP[method]]: true } : {}),
    }))
    validateField("methods", nextPaymentInfo)
    validateField(PAYMENT_METHOD_FIELD_MAP[method], nextPaymentInfo)
  }

  const updateHandle = (field: Exclude<keyof PaymentInfo, "methods">, value: string) => {
    const normalizedValue =
      field === "cashappHandle"
        ? normalizeCashAppHandle(value)
        : field === "venmoHandle"
          ? normalizeVenmoHandle(value)
          : value.trimStart()

    const nextPaymentInfo = { ...data.paymentInfo, [field]: normalizedValue }
    onUpdate({ paymentInfo: nextPaymentInfo })

    if (paymentTouched[field]) {
      validateField(field, nextPaymentInfo)
    }
  }

  const handleFieldBlur = (field: Exclude<keyof PaymentInfo, "methods">) => {
    markTouched(field)

    const currentValue = data.paymentInfo[field]
    const normalizedValue =
      field === "cashappHandle"
        ? normalizeCashAppHandle(currentValue)
        : field === "venmoHandle"
          ? normalizeVenmoHandle(currentValue)
          : normalizePaymentEmail(currentValue)

    const nextPaymentInfo = normalizedValue === currentValue
      ? data.paymentInfo
      : { ...data.paymentInfo, [field]: normalizedValue }

    if (normalizedValue !== currentValue) {
      onUpdate({ paymentInfo: nextPaymentInfo })
    }

    validateField(field, nextPaymentInfo)
  }

  return (
    <motion.div
      className="space-y-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.div className="text-center space-y-2" variants={fadeInUp}>
        <h2 className="text-2xl font-semibold text-white">Payment Information</h2>
        <p className="text-[#9A9ABF]">
          {isAgency
            ? "Set up where you'll receive payments for your agents"
            : "Choose how you'd like to receive your earnings"}
        </p>
      </motion.div>

      <motion.div className="space-y-4" variants={fadeInUp}>
        <Label className="text-[#9A9ABF]">Select payment methods (at least one) *</Label>
        <div className="grid grid-cols-2 gap-3">
          {paymentMethods.map((method, index) => {
            const isSelected = data.paymentInfo.methods.includes(method.value)
            return (
              <motion.div
                key={method.value}
                variants={fadeInUp}
                initial="initial"
                animate="animate"
                transition={{ delay: index * 0.03, duration: 0.2, ease: "easeOut" }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => toggleMethod(method.value)}
                className={`
                  relative cursor-pointer rounded-xl border p-4 transition-all duration-200
                  bg-[#1C1C32]  flex flex-col items-center gap-3
                  ${isSelected
                    ? "border-white/60 shadow-[0_0_16px_rgba(255,255,255,0.15)]"
                    : "border-[#2A2A45] hover:border-[#3A3A55] opacity-70 hover:opacity-90"
                  }
                `}
              >
                {/* Brand color circle with logo */}
                <div 
                  className={`
                    w-14 h-14 rounded-full flex items-center justify-center overflow-hidden transition-all duration-200
                    ${isSelected ? "scale-105" : "scale-100"}
                  `}
                  style={{ backgroundColor: method.brandColor }}
                >
                  <method.Logo />
                </div>
                
                {/* Label */}
                <div className="font-medium text-white text-sm">{method.label}</div>

                {/* Selected checkmark - white background */}
                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                      <Check className="w-4 h-4 text-gray-900" strokeWidth={3} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )
          })}
        </div>
        {renderFieldError("methods")}
      </motion.div>

      <AnimatePresence>
        {data.paymentInfo.methods.length > 0 && (
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {paymentMethods
              .filter((method) => data.paymentInfo.methods.includes(method.value))
              .map((method) => {
                const isEmailField = method.field === "zelleEmail" || method.field === "paypalEmail"
                const fieldValue = data.paymentInfo[method.field]

                return (
                  <motion.div
                    key={method.value}
                    className="space-y-1.5"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <Label htmlFor={method.field} className="text-[#9A9ABF]">
                      {method.label} {isEmailField ? "Email" : "Handle"} *
                    </Label>
                    <AnimatedInput
                      id={method.field}
                      type={isEmailField ? "email" : "text"}
                      placeholder={method.placeholder}
                      value={fieldValue}
                      onChange={(e) => updateHandle(method.field, e.target.value)}
                      onBlur={() => handleFieldBlur(method.field)}
                      autoCapitalize={isEmailField ? "off" : "none"}
                      autoCorrect="off"
                      spellCheck={false}
                      className={`text-base ${hasFieldError(method.field) ? "border-[#FF4757]" : ""}`}
                    />
                    <div className="flex items-center gap-2">
                      {renderFieldError(method.field)}
                      {isMethodValueValid(method.value) && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="flex items-center gap-1 text-[#32CD32] text-xs"
                        >
                          <Sparkles className="size-3" />
                          {isEmailField ? "Valid email" : "Valid handle"}
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                )
              })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
function CongratulationsStep({
  data,
  generatedAgencyId,
}: {
  data: OnboardingData
  generatedAgencyId: string | null
}) {
  const [copied, setCopied] = useState(false)
  const [showContent, setShowContent] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 300)
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ["#F37020", "#FF8C42", "#FFD700"],
    })
    return () => clearTimeout(timer)
  }, [])

  const copyToClipboard = () => {
    if (generatedAgencyId) {
      navigator.clipboard.writeText(generatedAgencyId)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const rules = [
    "Submit only valid, verified member IDs",
    "One member ID can only be submitted once across the system",
    "Points are awarded after verification (usually within 24 hours)",
    "Maintain professional conduct in all agency communications",
    "Report any issues or discrepancies to your agency manager",
  ]

  // Show different content for agents (they get the spin wheel)
  if (data.userType === "agent") {
    return (
      <motion.div
        className="space-y-6"
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        <motion.div
          className="text-center space-y-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#F37020] to-[#FF8C42] mb-4"
          >
            <Sparkles className="w-10 h-10 text-white" />
          </motion.div>

          <AnimatePresence>
            {showContent && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-2"
              >
                <h2 className="text-3xl font-semibold text-white">
                  You're All Set!
                </h2>
                <p className="text-[#9A9ABF]">
                  Welcome to {data.agencyName || "the team"}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence>
          {showContent && (
            <motion.div
              className="p-5 bg-gradient-to-br from-[#F37020]/20 to-[#FF8C42]/10 border border-[#F37020]/30 rounded-2xl text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              <p className="text-[#32CD32] font-semibold text-lg">A welcome spin awaits you!</p>
              <p className="text-sm text-[#9A9ABF] mt-1">Win $1 - $100 cash bonus</p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    )
  }

  // Default congratulations for agencies and solo agents
  return (
    <motion.div
      className="space-y-6"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.div
        className="text-center space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#F37020] to-[#FF8C42] mb-4"
        >
          <Sparkles className="w-10 h-10 text-white" />
        </motion.div>

        <AnimatePresence>
          {showContent && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2"
            >
              <h2 className="text-3xl font-semibold text-white">
                Congratulations!
              </h2>
              <p className="text-[#9A9ABF]">
                Your account has been set up successfully
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {showContent && generatedAgencyId && (
          <motion.div
            className="p-5 bg-gradient-to-br from-[#F37020]/20 to-[#FF8C42]/10 border border-[#F37020]/30 rounded-2xl text-center space-y-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <p className="text-sm text-[#9A9ABF]">Your Agency ID</p>
            <div className="flex items-center justify-center gap-3">
              <motion.p
                className="text-3xl font-mono font-semibold tracking-wider text-[#F37020]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {generatedAgencyId.split("").map((char, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.03 }}
                  >
                    {char}
                  </motion.span>
                ))}
              </motion.p>
              <motion.button
                onClick={copyToClipboard}
                className={`p-2 rounded-lg transition-colors ${
                  copied
                    ? "bg-green-500/20 text-[#32CD32]"
                    : "bg-[#F37020]/20 text-[#F37020] hover:bg-[#F37020]/30"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.2 }}
              >
                {copied ? (
                  <Check className="size-5" />
                ) : (
                  <Copy className="size-5" />
                )}
              </motion.button>
            </div>
            <AnimatePresence>
              {copied && (
                <motion.p
                  className="text-sm text-[#32CD32] font-medium"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  Copied to clipboard!
                </motion.p>
              )}
            </AnimatePresence>
            <p className="text-sm text-[#9A9ABF]">
              Share this with agents who want to join your agency
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showContent && (
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.3 }}
          >
            <h3 className="font-semibold text-center text-white">Agent Rules & Guidelines</h3>
            <ul className="space-y-2">
              {rules.map((rule, index) => (
                <motion.li
                  key={index}
                  className="flex items-start gap-3 text-sm p-2 rounded-lg hover:bg-[#1C1C32] transition-colors"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.05, duration: 0.2 }}
                >
                  <motion.div
                    className="size-5 rounded-full bg-[#F37020]/20 flex items-center justify-center shrink-0 mt-0.5"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.55 + index * 0.05, duration: 0.15 }}
                  >
                    <Sparkles className="size-3 text-[#F37020]" />
                  </motion.div>
                  <span className="text-white">{rule}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// Main Onboarding Component
export function Onboarding() {
  const [step, setStep] = useState(0)
  const [direction, setDirection] = useState(1)
  const [data, setData] = useState<OnboardingData>(initialData)
  const [generatedAgencyId, setGeneratedAgencyId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [agencyValidation, setAgencyValidation] = useState<{
    isValid: boolean | null
    agencyName: string | null
    error: string | null
    isValidating: boolean
  }>({ isValid: null, agencyName: null, error: null, isValidating: false })
  const [agencyNameStatus, setAgencyNameStatus] = useState<{
    available: boolean | null
    checking: boolean
  }>({ available: null, checking: false })
  const { completeOnboarding, validateAgencyId, checkAgencyName } = useAuth()

  // Debounced agency name availability check
  useEffect(() => {
    const name = data.agencyName.trim()
    if (name.length < 2) {
      setAgencyNameStatus({ available: null, checking: false })
      return
    }

    setAgencyNameStatus((prev) => ({ ...prev, checking: true }))
    const timeout = setTimeout(async () => {
      const result = await checkAgencyName(name)
      setAgencyNameStatus({ available: result.available, checking: false })
    }, 500)

    return () => clearTimeout(timeout)
  }, [data.agencyName])

  const updateData = (updates: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...updates }))
    if (updates.agencyId !== undefined) {
      setAgencyValidation({ isValid: null, agencyName: null, error: null, isValidating: false })
    }
  }

  const handleValidateAgency = async (agencyId: string) => {
    if (!agencyId || agencyId.trim().length < 4) return

    setAgencyValidation({ isValid: null, agencyName: null, error: null, isValidating: true })

    const result = await validateAgencyId(agencyId)

    setAgencyValidation({
      isValid: result.success,
      agencyName: result.agencyName || null,
      error: result.success ? null : result.message || "Invalid agency ID",
      isValidating: false,
    })

    if (result.success && result.agencyName && data.userType === "agent") {
      setData((prev) => ({ ...prev, agencyName: result.agencyName || "" }))
    }
  }

  const getSteps = useCallback((): { component: React.ReactNode; key: string }[] => {
    const steps = [
      { component: <UserTypeStep data={data} onUpdate={updateData} />, key: "user-type" },
    ]

    if (!data.userType) return steps

    if (data.userType === "agent") {
      steps.push({
        component: (
          <AgencyIdStep
            data={data}
            onUpdate={updateData}
            agencyValidation={agencyValidation}
            onValidateAgency={handleValidateAgency}
          />
        ),
        key: "agency-id",
      })
    }

    if (data.userType === "agent") {
      steps.push({
        component: <DisplayNameStep data={data} onUpdate={updateData} />,
        key: "display-name",
      })
      steps.push({
        component: <PaymentInfoStep data={data} onUpdate={updateData} />,
        key: "payment",
      })
    } else {
      steps.push({
        component: <AgencyNameStep data={data} onUpdate={updateData} nameStatus={agencyNameStatus} />,
        key: "agency-name",
      })

      if (data.userType === "agency") {
        steps.push({
          component: <PaymentCollectionStep data={data} onUpdate={updateData} />,
          key: "payment-collection",
        })
        if (data.collectOnBehalf) {
          steps.push({
            component: <PaymentInfoStep data={data} onUpdate={updateData} isAgency />,
            key: "payment",
          })
        }
      } else {
        steps.push({
          component: <PaymentInfoStep data={data} onUpdate={updateData} />,
          key: "payment",
        })
      }
    }

    steps.push({
      component: <W9Step data={data} onUpdate={updateData} />,
      key: "w9",
    })

    steps.push({
      component: (
        <CongratulationsStep data={data} generatedAgencyId={generatedAgencyId} />
      ),
      key: "congrats",
    })

    return steps
  }, [data, generatedAgencyId, agencyValidation, handleValidateAgency, agencyNameStatus])

  const steps = getSteps()
  const totalSteps = steps.length
  const isLastStep = step === totalSteps - 1
  const isFirstStep = step === 0

  const canProceed = (): boolean => {
    const currentStepKey = steps[step]?.key

    switch (currentStepKey) {
      case "user-type":
        return !!data.userType
      case "agency-id":
        if (data.userType === "agent") {
          return agencyIdSchema.safeParse(data.agencyId).success && agencyValidation.isValid === true
        }
        if (!data.isIndependent && data.agencyId.trim().length > 0) {
          return agencyIdSchema.safeParse(data.agencyId).success && agencyValidation.isValid === true
        }
        return data.isIndependent || data.agencyId.trim().length > 0
      case "w9": {
        return w9Schema.safeParse(prepareW9ForValidation(data.w9Info)).success
      }
      case "display-name":
        return getDisplayNameValidationSchema(data.userType).safeParse(data.displayName).success
      case "agency-name":
        return (
          agencyNameSchema.safeParse(data.agencyName).success &&
          agencyNameStatus.available === true &&
          !agencyNameStatus.checking
        )
      case "payment-collection":
        return true
      case "payment":
        return paymentSchema.safeParse(preparePaymentForValidation(data.paymentInfo)).success
      case "congrats":
        return true
      default:
        return true
    }
  }

  const handleNext = async () => {
    if (isLastStep) {
      setIsSubmitting(true)
      try {
        const finalData = {
          ...data,
          agencyId: generatedAgencyId || data.agencyId,
          displayName: data.displayName.trim(),
          agencyName: data.agencyName.trim(),
          paymentInfo: preparePaymentForValidation(data.paymentInfo),
        }
        const result = await completeOnboarding(finalData, generatedAgencyId || undefined)
        if (!result.success) {
          console.error("Failed to complete onboarding:", result.message)
        }
      } catch (error) {
        console.error("Error completing onboarding:", error)
      } finally {
        setIsSubmitting(false)
      }
      return
    }

    const nextStepKey = steps[step + 1]?.key

    if (nextStepKey === "congrats") {
      if (
        data.userType === "agency" ||
        (data.userType === "solo_agent" && data.isIndependent)
      ) {
        setGeneratedAgencyId(generateAgencyId())
      }
    }

    setDirection(1)
    setStep(step + 1)
  }

  const handleBack = () => {
    if (!isFirstStep) {
      setDirection(-1)
      setStep(step - 1)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 overflow-hidden relative bg-[#121220]" style={{ fontFamily: "'Inter Tight', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      {/* Subtle gradient overlay */}

      {/* Logo */}
      <motion.div
        className="mb-6 relative z-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <img src={ovalLogo} alt="Oval" className="h-12" style={{ filter: "brightness(0.9) sepia(0.2) saturate(0.8)" }} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
        className="w-full max-w-lg relative z-10 mx-2 sm:mx-0"
      >
        {/* Main Card - dark glass morphism */}
        <div className="bg-[#1C1C32] border border-[#2A2A45] rounded-2xl overflow-hidden">
          {/* Header - dark with orange accents */}
          <div className="bg-[#1C1C32] px-4 sm:px-6 py-4 border-b border-[#2A2A45]">
            <div className="flex items-center justify-between">
              <span className="text-white font-medium text-sm">
                Step {step + 1} of {totalSteps}
              </span>
              <div className="flex gap-1.5">
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <motion.div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i <= step ? "bg-[#F37020]" : "bg-white/20"
                    }`}
                    animate={i === step ? { scale: [1, 1.2, 1] } : {}}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                ))}
              </div>
            </div>

            {/* Progress Bar - orange fill */}
            <div className="mt-3 h-1.5 bg-[#1A1A30] rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#F37020] to-[#FF8C42] rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: `${((step + 1) / totalSteps) * 100}%` }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="min-h-[380px] sm:min-h-[420px] overflow-hidden overflow-y-auto px-4 sm:px-6 py-6">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={steps[step]?.key ?? step}
                custom={direction}
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                {steps[step]?.component}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className={`flex pt-4 px-4 sm:px-6 pb-6 ${isLastStep ? "justify-center" : "justify-between"} border-t border-[#2A2A45]`}>
            {!isLastStep && (
              <GlowButton
                variant="outline"
                onClick={handleBack}
                disabled={isFirstStep}
                className="gap-2"
              >
                <ArrowLeft className="size-4" />
                Back
              </GlowButton>
            )}
            <GlowButton
              onClick={handleNext}
              disabled={!canProceed() || isSubmitting}
              className="gap-2 min-w-[140px]"
            >
              {isLastStep ? (
                isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                    Saving...
                  </>
                ) : (
                  <>
                    <Sparkles className="size-5" />
                    Complete!
                  </>
                )
              ) : (
                <>
                  Continue
                  <ArrowRight className="size-5" />
                </>
              )}
            </GlowButton>
          </div>
        </div>
      </motion.div>

      {/* Footer tagline - dark styled */}
      <motion.div
        className="mt-6 relative z-10"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.3 }}
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1C1C32] border border-[#2A2A45] rounded-full">
          <span className="text-sm text-[#9A9ABF] flex items-center gap-1.5">
            Powered by
            <img src={ovalLogo} alt="Oval" className="h-4 inline-block opacity-80" />
          </span>
        </div>
      </motion.div>
    </div>
  )
}
